import React, {forwardRef, memo, useRef} from 'react';
import MaterialTable from "material-table";
import TableIcons from "../../utils/TableIcons";
import RefreshIcon from "@material-ui/icons/Refresh";
import ClearIcon from '@material-ui/icons/Clear';
import CheckIcon from '@material-ui/icons/Check';
import SyncIcon from "@material-ui/icons/Sync";
import AddBox from "@material-ui/icons/AddBox";
import {Autocomplete} from '@material-ui/lab';
import getEnvironment from "../../utils/getEnvironment";
import {Button, List, ListItem, ListItemText, Switch, TextField, Typography} from "@material-ui/core";
import theme from '../../styles/theme';
import useStyles from '../../styles/styles';
import useFilterData from '../../hooks/useFilterData';
import _ from 'lodash';

const AdvertiserTable = memo(({
                                  data,
                                  refetch,
                                  setSuccess,
                                  createAdvertiser,
                                  createAdvertiserLoading
                              }) => {
    const classes = useStyles();
    const {allCompanies, allAdvertiser, allOffer} = data;
    const {findGraphqlData, filterGraphqlData} = useFilterData();
    const env = getEnvironment();
    const tableRef = useRef();

    const handleKey = (e) => {
        if (e.key === 'Enter') {
            e.stopPropagation()
        }
    }

    return (
        <>
            <MaterialTable
                title=""
                tableRef={tableRef}
                icons={{
                    ...TableIcons,
                    Add: forwardRef((props, ref) => {
                        return (
                            <>
                                {env !== 'prod' ? (
                                    <AddBox {...props} ref={ref}/>
                                ) : ""}
                            </>
                        );
                    }),
                }}
                columns={[
                    {title: 'Id', field: "id", hidden: true},
                    {
                        title: "Name", field: "name",
                        editComponent: props => (
                            <TextField
                                value={props.value}
                                type="text"
                                onChange={e => props.onChange(e.target.value)}
                                error={props.value === ''}
                                onKeyDown={e => handleKey(e)}
                            />
                        )
                    },
                    {
                        title: "Key", field: "key",
                        editComponent: props => (
                            <TextField
                                value={props.value}
                                type="text"
                                onChange={e => props.onChange(e.target.value)}
                                error={props.value === ''}
                                onKeyDown={e => handleKey(e)}
                            />
                        )
                    },
                    {
                        title: "Masked Name", field: "maskedName",
                        render: rowData => rowData.maskedName !== null && rowData.maskedName !== '' ? rowData.maskedName : 'N/A',
                        editComponent: props => (
                            <TextField
                                value={props.value}
                                type="text"
                                onChange={e => props.onChange(e.target.value)}
                                error={props.value === ''}
                                onKeyDown={e => handleKey(e)}
                            />
                        )
                    },
                    {
                        title: "Test", field: "isTest", width: '20px',
                        render: rowData => rowData.isTest ? <CheckIcon style={{color: theme.palette.success.main}}/> :
                            <ClearIcon style={{color: theme.palette.error.main}}/>,
                        editComponent: props => (
                            <Switch
                                onChange={e => props.onChange(e.target.checked)}
                                checked={props.value}
                                inputProps={{'aria-label': 'primary checkbox'}}
                            />
                        )
                    },
                    {
                        title: "Company", field: "companyId",
                        render: rowData => findGraphqlData(allCompanies, rowData.companyId, "id", "name"),
                        editComponent: props => (
                            <Autocomplete
                                size="medium"
                                className={classes.autoComplete}
                                value={props.value}
                                options={allCompanies}
                                renderOption={option => (
                                    <ListItem divider dense>
                                        <ListItemText style={{padding: 0}}>
                                            <strong>{option.name}</strong> <br/>
                                            <small>{option.id}</small>
                                        </ListItemText>
                                    </ListItem>
                                )}
                                getOptionLabel={(option) => {
                                    if (_.isObject(option)) {
                                        return findGraphqlData(allCompanies, option.id, "id", "name")
                                    }
                                    return findGraphqlData(allCompanies, option, "id", "name");
                                }}
                                onChange={(e, newValue) => props.onChange(newValue.id)}
                                onKeyDown={e => handleKey(e)}
                                renderInput={(params) => <TextField {...params} label="Company" fullWidth/>}
                            />
                        )
                    },
                    {
                        title: 'Offers Linked', field: 'offers', editable: "never",
                        render: (rowData) => {
                            if (rowData.offers.length) {
                                return (
                                    <List dense style={{height: '250px', overflow: 'scroll'}}>
                                        {rowData.offers.map((item, idx) => {
                                            const isLast = rowData.offers.length === idx + 1;
                                            return (
                                                <>
                                                    <ListItem
                                                        key={item.id}
                                                        divider
                                                        alignItems={"flex-start"}
                                                        disableGutters
                                                    >
                                                        <ListItemText disableTypography>
                                                            <Typography variant="body2">
                                                                <a href={`/info/offers/${item.id}`} target="_blank">
                                                                    {item.offerName}
                                                                </a>
                                                            </Typography>
                                                        </ListItemText>
                                                    </ListItem>
                                                    {isLast ? (
                                                        <ListItem
                                                            divider
                                                            alignItems={"flex-start"}
                                                            disableGutters
                                                        >
                                                            <ListItemText disableTypography>
                                                                <Typography variant="body2">
                                                                    <a href={`/info/offers/advertiser/${item.advertiserId}`}
                                                                       target="_blank">
                                                                        View All Offers by Advertiser
                                                                    </a>
                                                                </Typography>
                                                            </ListItemText>
                                                        </ListItem>
                                                    ) : null}
                                                </>
                                            );
                                        })}
                                    </List>
                                );
                            }

                            return 'N/A';
                        }
                    }
                ]}
                data={allAdvertiser.map(advertiser => ({
                    id: advertiser.id,
                    name: advertiser.name,
                    key: advertiser.key,
                    maskedName: advertiser.maskedName,
                    isTest: advertiser.isTest,
                    companyId: advertiser.companyId,
                    offers: filterGraphqlData(allOffer, advertiser.id, "advertiserId")
                }))}
                editable={{
                    onRowAdd: newData =>
                        new Promise((resolve, reject) => {
                            const {isTest} = newData;
                            setTimeout(() => {
                                delete newData.offers
                                createAdvertiser({
                                    variables: {
                                        input: {
                                            ...newData,
                                            isTest: isTest || false
                                        }
                                    }
                                });

                                if (!createAdvertiserLoading) {
                                    setTimeout(() => refetch(), 3000);
                                    resolve();
                                }

                            }, 1000);
                        }),
                    onRowUpdate: env === 'test' ? (newData, oldData) =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                delete newData.offers
                                createAdvertiser({
                                    variables: {
                                        input: {
                                            ...newData
                                        }
                                    }
                                });

                                if (!createAdvertiserLoading) {
                                    setTimeout(() => refetch(), 3000);
                                    resolve();
                                }
                            }, 1000);
                        }) : null,
                    onRowDelete: null,
                }}
                actions={[
                    {
                        icon: () => <RefreshIcon/>,
                        tooltip: 'Refetch Data',
                        isFreeAction: true,
                        onClick: () => refetch()
                    },
                ]}
                options={{
                    addRowPosition: 'first',
                    filtering: true,
                    pageSize: 300,
                    pageSizeOptions: [300, 600, 800],
                    headerStyle: {
                        minWidth: 'auto',
                    },
                }}
            />
        </>
    );

});

export default AdvertiserTable;